import SecureTokenStorage from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface BurningEntity {
  id: string;
  name: string;
  type: 'USER' | 'BUSINESS';
  location: string;
  heatScore: number;
  complaints: number;
  recentActivity: number;
  lastActivity: string;
  status: 'NORMAL' | 'HEATING' | 'HOT' | 'BURNING' | 'COOLING' | 'FROZEN';
  verified: boolean;
  trustScore: number;
  description?: string;
  industry?: string;
  heatTrend: 'RISING' | 'STABLE' | 'DECLINING';
  daysInCurrentStatus: number;
  profileImage?: string;
  contactInfo?: {
    email?: string;
    phone?: string;
    website?: string;
  };
}

export interface BurningIncident {
  id: string;
  entityType: 'USER' | 'BUSINESS';
  entityId: string;
  incidentType: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  title: string;
  description: string;
  evidenceUrls: string[];
  reportedBy: string;
  reportedAt: string;
  status: 'PENDING' | 'VERIFIED' | 'DISMISSED' | 'RESOLVED';
}

export interface HeatScoreHistory {
  date: string;
  score: number;
  change: number;
  reason: string;
}

export interface BurningPageFilters {
  page?: number;
  limit?: number;
  entityType?: 'all' | 'USER' | 'BUSINESS';
  sortBy?: 'heat' | 'complaints' | 'recent';
  location?: string;
  minHeatScore?: number;
  status?: 'NORMAL' | 'HEATING' | 'HOT' | 'BURNING' | 'COOLING' | 'FROZEN';
}

class BurningPageService {
  private static getAuthHeaders() {
    const tokens = SecureTokenStorage.getTokens();
    return {
      'Content-Type': 'application/json',
      Authorization: tokens?.accessToken ? `Bearer ${tokens.accessToken}` : '',
    };
  }

  /**
   * Get burning entities with filtering and pagination
   */
  static async getBurningEntities(filters: BurningPageFilters = {}): Promise<{
    entities: BurningEntity[];
    pagination: {
      currentPage: number;
      totalCount: number;
      hasMore: boolean;
      limit: number;
    };
  }> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.entityType && filters.entityType !== 'all')
      params.append('entityType', filters.entityType);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.location) params.append('location', filters.location);
    if (filters.minHeatScore)
      params.append('minHeatScore', filters.minHeatScore.toString());
    if (filters.status) params.append('status', filters.status);

    const response = await fetch(
      `${API_BASE_URL}/v1/burning-page/entities?${params}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch burning entities');
    }

    return data.data;
  }

  /**
   * Get heat score history for a specific entity
   */
  static async getEntityHeatHistory(
    entityType: 'USER' | 'BUSINESS',
    entityId: string,
    days = 30
  ): Promise<HeatScoreHistory[]> {
    const response = await fetch(
      `${API_BASE_URL}/v1/burning-page/entities/${entityType}/${entityId}/heat-history?days=${days}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch heat history');
    }

    return data.data.history;
  }

  /**
   * Get recent burning incidents for an entity
   */
  static async getEntityIncidents(
    entityType: 'USER' | 'BUSINESS',
    entityId: string,
    page = 1,
    limit = 10
  ): Promise<{
    incidents: BurningIncident[];
    totalCount: number;
    hasMore: boolean;
  }> {
    const response = await fetch(
      `${API_BASE_URL}/v1/burning-page/entities/${entityType}/${entityId}/incidents?page=${page}&limit=${limit}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch entity incidents');
    }

    return data.data;
  }

  /**
   * Report a new burning incident
   */
  static async reportIncident(incidentData: {
    entityType: 'USER' | 'BUSINESS';
    entityId: string;
    incidentType: string;
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    title: string;
    description: string;
    evidenceUrls?: string[];
  }): Promise<BurningIncident> {
    const response = await fetch(`${API_BASE_URL}/v1/burning-page/incidents`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(incidentData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to report incident');
    }

    return data.data;
  }

  /**
   * Get trending burning entities
   */
  static async getTrendingEntities(limit = 10): Promise<BurningEntity[]> {
    const response = await fetch(
      `${API_BASE_URL}/v1/burning-page/trending?limit=${limit}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch trending entities');
    }

    return data.data.entities;
  }

  /**
   * Get burning page analytics
   */
  static async getBurningAnalytics(
    entityType?: 'USER' | 'BUSINESS',
    timeRange = '7d'
  ): Promise<{
    totalEntities: number;
    burningEntities: number;
    hotEntities: number;
    heatingEntities: number;
    averageHeatScore: number;
    trendData: Array<{
      date: string;
      burning: number;
      hot: number;
      heating: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (entityType) params.append('entityType', entityType);
    params.append('timeRange', timeRange);

    const response = await fetch(
      `${API_BASE_URL}/v1/burning-page/analytics?${params}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch burning analytics');
    }

    return data.data;
  }

  /**
   * Search burning entities
   */
  static async searchEntities(
    query: string,
    filters: Omit<BurningPageFilters, 'page' | 'limit'> = {}
  ): Promise<BurningEntity[]> {
    const params = new URLSearchParams();
    params.append('q', query);

    if (filters.entityType && filters.entityType !== 'all')
      params.append('entityType', filters.entityType);
    if (filters.location) params.append('location', filters.location);
    if (filters.minHeatScore)
      params.append('minHeatScore', filters.minHeatScore.toString());

    const response = await fetch(
      `${API_BASE_URL}/v1/burning-page/search?${params}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to search entities');
    }

    return data.data.entities;
  }

  /**
   * Get heat status color for UI
   */
  static getStatusColor(status: string): string {
    switch (status) {
      case 'BURNING':
        return 'text-red-600 bg-red-100';
      case 'HOT':
        return 'text-orange-600 bg-orange-100';
      case 'HEATING':
        return 'text-yellow-600 bg-yellow-100';
      case 'COOLING':
        return 'text-blue-600 bg-blue-100';
      case 'FROZEN':
        return 'text-purple-600 bg-purple-100';
      case 'NORMAL':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  /**
   * Get heat trend color for UI
   */
  static getTrendColor(trend: string): string {
    switch (trend) {
      case 'RISING':
        return 'text-red-600';
      case 'DECLINING':
        return 'text-green-600';
      case 'STABLE':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  }

  /**
   * Format heat score for display
   */
  static formatHeatScore(score: number): string {
    if (score >= 90) return `🔥 ${score}`;
    if (score >= 70) return `🌡️ ${score}`;
    if (score >= 50) return `⚠️ ${score}`;
    return `✅ ${score}`;
  }

  /**
   * Get relative time string
   */
  static getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  }
}

export default BurningPageService;
